import { useEffect, useRef, useState } from 'react'

import type { ChartApi } from '@/components/PriceChart'
import {
  loadDrawings,
  newId,
  pipsBetween,
  saveDrawings,
  type Drawing,
  type DrawingKind,
  type DrawingPoint,
} from '@/lib/drawings'

/**
 * Calque de dessin : niveaux horizontaux, segments, et règle à pips.
 *
 * Deux principes gouvernent ce composant.
 *
 * **Les tracés sont ancrés sur des prix et des instants, jamais sur des
 * pixels.** Une résistance reste sur son prix quand on zoome, et un segment
 * continue de relier les deux mêmes bougies après un défilement. Les
 * coordonnées écran sont recalculées à chaque image.
 *
 * **Le calque est transparent aux gestes tant qu'aucun outil n'est armé.**
 * Sans cela, il intercepterait molette, pincement et glissement, et le
 * graphique paraîtrait figé — c'est exactement le défaut qu'avait le calque
 * des niveaux SL/TP avant correction.
 */
export function ChartDrawings({
  api,
  symbol,
  pipSize,
  digits,
  height,
  tool,
  color,
  onFinished,
}: {
  api: ChartApi | null
  symbol: string
  pipSize: number
  digits: number
  height: number
  /** Outil armé. `null` = curseur : le calque laisse tout passer. */
  tool: DrawingKind | null
  color: string
  /** Appelé quand un tracé est terminé, pour désarmer l'outil. */
  onFinished: () => void
}) {
  const [drawings, setDrawings] = useState<Drawing[]>([])
  const [pending, setPending] = useState<DrawingPoint | null>(null)
  const [hover, setHover] = useState<DrawingPoint | null>(null)
  const [selected, setSelected] = useState<string | null>(null)
  const [, redraw] = useState(0)
  const overlay = useRef<HTMLDivElement>(null)
  // Dernière géométrie dessinée, et les valeurs vivantes que la boucle
  // d'animation consulte. L'ancre et l'aperçu du tracé en cours sont pilotés
  // par le pointeur, donc déjà par React : seuls les tracés posés ont besoin
  // d'être suivis image par image.
  const geometry = useRef('')
  const snapshot = useRef({ api, drawings })
  snapshot.current = { api, drawings }

  // Les tracés changent de paire en même temps que le graphique.
  useEffect(() => {
    setDrawings(loadDrawings(symbol))
    setPending(null)
    setSelected(null)
  }, [symbol])

  /*
   * Le graphique bouge tout seul (zoom, défilement, nouvelle bougie) : on
   * REGARDE à chaque image, mais on ne redessine que si les tracés ont
   * réellement changé de place à l'écran.
   *
   * Voir la note du calque des niveaux : redessiner inconditionnellement
   * coûtait soixante rendus React par seconde et par calque, sur un graphique
   * immobile et même quand aucun tracé n'existe. C'est le défaut qui rendait
   * toute l'application poussive sur téléphone.
   */
  useEffect(() => {
    let frame = 0
    const tick = () => {
      const current = snapshot.current
      let signature = ''
      if (current.api) {
        for (const drawing of current.drawings) {
          for (const point of drawing.points) {
            signature += `|${Math.round(current.api.timeToX(point.time) ?? -1)},${Math.round(
              current.api.priceToY(point.price) ?? -1,
            )}`
          }
        }
      }
      if (signature !== geometry.current) {
        geometry.current = signature
        redraw((n) => (n + 1) % 1000)
      }
      frame = requestAnimationFrame(tick)
    }
    frame = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frame)
  }, [])

  function persist(next: Drawing[]) {
    setDrawings(next)
    saveDrawings(symbol, next)
  }

  function pointAt(event: React.PointerEvent): DrawingPoint | null {
    const bounds = overlay.current?.getBoundingClientRect()
    if (!bounds || !api) return null
    const price = api.yToPrice(event.clientY - bounds.top)
    const time = api.xToTime(event.clientX - bounds.left)
    if (price === null || time === null) return null
    return { time, price }
  }

  function handleClick(event: React.PointerEvent) {
    if (!tool) return
    const point = pointAt(event)
    if (point === null) return

    if (tool === 'level') {
      persist([...drawings, { id: newId(), kind: 'level', color, points: [point] }])
      onFinished()
      return
    }

    // Segment et règle : deux clics. Le premier pose l'ancre, le second ferme.
    if (pending === null) {
      setPending(point)
      return
    }
    persist([...drawings, { id: newId(), kind: tool, color, points: [pending, point] }])
    setPending(null)
    onFinished()
  }

  function handleMove(event: React.PointerEvent) {
    if (!tool || pending === null) return
    setHover(pointAt(event))
  }

  if (!api) return null

  const xy = (point: DrawingPoint) => {
    const x = api.timeToX(point.time)
    const y = api.priceToY(point.price)
    return x === null || y === null ? null : { x, y }
  }

  /** Tracé en cours de construction, suivi par le pointeur. */
  const preview =
    pending !== null && hover !== null && tool && tool !== 'level'
      ? { id: '__preview__', kind: tool, color, points: [pending, hover] }
      : null

  return (
    <div
      ref={overlay}
      onPointerDown={handleClick}
      onPointerMove={handleMove}
      // `pointer-events` seulement quand un outil est armé : sinon le calque
      // volerait la molette et le glissement au graphique.
      className={`absolute inset-0 z-10 ${
        tool ? 'pointer-events-auto cursor-crosshair touch-none' : 'pointer-events-none'
      }`}
      style={{ height }}
    >
      <svg className="h-full w-full" style={{ overflow: 'visible' }}>
        {[...drawings, ...(preview ? [preview as Drawing] : [])].map((drawing) => {
          const isPreview = drawing.id === '__preview__'
          const actif = selected === drawing.id

          if (drawing.kind === 'level') {
            const y = api.priceToY(drawing.points[0].price)
            if (y === null) return null
            return (
              <g key={drawing.id}>
                <line
                  x1={0}
                  x2="100%"
                  y1={y}
                  y2={y}
                  stroke={drawing.color}
                  strokeWidth={actif ? 3 : 1.5}
                />
                {/* Bande d'accroche invisible : une ligne d'un pixel et demi
                    est impossible à viser au doigt. */}
                <line
                  x1={0}
                  x2="100%"
                  y1={y}
                  y2={y}
                  stroke="transparent"
                  strokeWidth={20}
                  className="pointer-events-auto cursor-pointer"
                  onPointerDown={(event) => {
                    if (tool) return
                    event.stopPropagation()
                    setSelected(actif ? null : drawing.id)
                  }}
                />
                <text
                  x={6}
                  y={y - 5}
                  fill={drawing.color}
                  className="num"
                  style={{ fontSize: 10, fontWeight: 600 }}
                >
                  {drawing.points[0].price.toFixed(digits)}
                </text>
              </g>
            )
          }

          const a = xy(drawing.points[0])
          const b = xy(drawing.points[1])
          if (!a || !b) return null
          const pips = pipsBetween(drawing.points[0].price, drawing.points[1].price, pipSize)
          const monte = drawing.points[1].price >= drawing.points[0].price

          return (
            <g key={drawing.id}>
              <line
                x1={a.x}
                y1={a.y}
                x2={b.x}
                y2={b.y}
                stroke={drawing.color}
                strokeWidth={actif ? 3 : 1.5}
                strokeDasharray={drawing.kind === 'ruler' ? '5 4' : undefined}
                opacity={isPreview ? 0.6 : 1}
              />
              {!isPreview && (
                <line
                  x1={a.x}
                  y1={a.y}
                  x2={b.x}
                  y2={b.y}
                  stroke="transparent"
                  strokeWidth={20}
                  className="pointer-events-auto cursor-pointer"
                  onPointerDown={(event) => {
                    if (tool) return
                    event.stopPropagation()
                    setSelected(actif ? null : drawing.id)
                  }}
                />
              )}
              {/* La mesure est portée par la règle, et par le segment aussi :
                  connaître l'amplitude d'une tendance vaut autant que de la
                  voir. */}
              <text
                x={(a.x + b.x) / 2}
                y={(a.y + b.y) / 2 - 8}
                fill={drawing.color}
                textAnchor="middle"
                className="num"
                style={{ fontSize: 11, fontWeight: 700 }}
              >
                {monte ? '+' : '−'}
                {pips.toFixed(1)} pips
              </text>
            </g>
          )
        })}

        {/* Ancre du tracé en cours : sans elle, on ne sait plus où l'on a
            cliqué la première fois. */}
        {pending && xy(pending) && (
          <circle cx={xy(pending)!.x} cy={xy(pending)!.y} r={4} fill={color} />
        )}
      </svg>

      {/* Suppression du tracé sélectionné. */}
      {selected && (
        <button
          type="button"
          onPointerDown={(event) => {
            event.stopPropagation()
            persist(drawings.filter((drawing) => drawing.id !== selected))
            setSelected(null)
          }}
          className="pointer-events-auto absolute right-2 top-2 min-h-touch rounded-md border border-down/60 bg-panel px-3 text-[12px] font-semibold text-down"
        >
          Supprimer ce tracé
        </button>
      )}
    </div>
  )
}
